import React, { useState, useEffect } from "react";
import { toast } from "react-toastify";
import ClubModal from "./ClubModal";
import uploadToDropbox from "../../../services/dropboxService";
import { SearchInput, ConfirmDialog } from "../../admin";

const ClubsTab = () => {
  const [clubs, setClubs] = useState([]);
  const [sports, setSports] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [showClubModal, setShowClubModal] = useState(false);
  const [selectedClub, setSelectedClub] = useState(null);
  const [clubToDelete, setClubToDelete] = useState(null);
  const [search, setSearch] = useState("");
  const [sportFilter, setSportFilter] = useState("all");

  // Fetch clubs and sports on component mount
  useEffect(() => {
    fetchClubs();
    fetchSports();
  }, []);

  const fetchClubs = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${process.env.REACT_APP_API_URL}/clubs`);
      if (!response.ok) throw new Error("Failed to fetch clubs");
      const data = await response.json();
      setClubs(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const fetchSports = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/sports`);
      if (!response.ok) throw new Error("Failed to fetch sports");
      const data = await response.json();
      setSports(data);
    } catch (err) {
      setError(err.message);
    }
  };

  const handleAddClub = async (newClub) => {
    try {
      setLoading(true);

      // Create a copy of the club data to modify
      const clubData = { ...newClub };

      // If there's a logo file, upload it first
      if (clubData.logo) {
        try {
          const logoUrl = await uploadToDropbox(clubData.logo);
          if (!logoUrl) throw new Error("Failed to upload logo");
          clubData.logo = logoUrl;
        } catch (uploadError) {
          console.error("Logo upload failed:", uploadError);
          toast.error("Failed to upload logo");
          return;
        }
      }
      // delete clubData.logo; // Remove the file object before sending to API

      const response = await fetch(`${process.env.REACT_APP_API_URL}/clubs`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(clubData), // Send the modified clubData instead of newClub
      });
      if (!response.ok) throw new Error("Failed to add club");
      const data = await response.json();
      setClubs([...clubs, data]);
      setShowClubModal(false);
      toast.success("Club added successfully!");
    } catch (err) {
      setError(err.message);
      toast.error(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleEditClub = async (club) => {
    try {
      setLoading(true);

      // Create a copy of the club data to modify
      const clubData = { ...club };

      // If there's a new logo file, upload it first
      if (clubData.logo) {
        try {
          const logoUrl = await uploadToDropbox(clubData.logo);
          if (!logoUrl) throw new Error("Failed to upload logo");
          clubData.logo = logoUrl;
        } catch (uploadError) {
          console.error("Logo upload failed:", uploadError);
          toast.error("Failed to upload logo");
          return;
        }
      }
      // delete clubData.logo; // Remove the file object before sending to API

      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/clubs/${clubData.clubId}`,
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(clubData), // Send the modified clubData instead of club
        }
      );
      if (!response.ok) throw new Error("Failed to update club");
      const updatedClub = await response.json();
      setClubs(
        clubs.map((c) => (c.clubId === clubData.clubId ? updatedClub : c))
      );
      setShowClubModal(false);
      setSelectedClub(null);
      toast.success("Club updated successfully!");
    } catch (err) {
      setError(err.message);
      toast.error(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteClub = async () => {
    if (!clubToDelete) return;
    const club = clubToDelete;

    try {
      setLoading(true);
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/clubs/${club.clubId}`,
        {
          method: "DELETE",
        }
      );
      const result = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(result.message || "Failed to delete club");
      }
      setClubs((currentClubs) => currentClubs.filter((c) => c.clubId !== club.clubId));
      setClubToDelete(null);
      toast.success(result.message || "Club deleted successfully!");
    } catch (err) {
      setError(err.message);
      toast.error(err.message);
    } finally {
      setLoading(false);
    }
  };

  const getSportName = (sportId) => {
    const sport = sports.find((s) => s.sportId === sportId);
    return sport ? sport.name : "N/A";
  };

  /* Derived: search + sport filter */
  const filtered = clubs.filter((club) => {
    if (sportFilter !== "all" && String(club.sportId) !== String(sportFilter)) return false;
    if (!search) return true;
    const q = search.toLowerCase();
    return (
      club.name?.toLowerCase().includes(q) ||
      club.location?.toLowerCase().includes(q) ||
      club.description?.toLowerCase().includes(q) ||
      getSportName(club.sportId)?.toLowerCase().includes(q)
    );
  });

  return (
    <div className="space-y-4">
      <div className="flex flex-col sm:flex-row sm:items-center gap-3">
        <SearchInput
          value={search}
          onChange={setSearch}
          placeholder="Search clubs by name, location or description…"
        />
        <select
          value={sportFilter}
          onChange={(e) => setSportFilter(e.target.value)}
          className="field !w-auto shrink-0"
        >
          <option value="all">All sports</option>
          {sports.map((sport) => (
            <option key={sport.sportId} value={sport.sportId}>
              {sport.name}
            </option>
          ))}
        </select>
        {(search || sportFilter !== "all") && (
          <button
            type="button"
            className="btn-ghost shrink-0"
            onClick={() => {
              setSearch("");
              setSportFilter("all");
            }}
          >
            Clear
          </button>
        )}
        <button
          type="button"
          className="btn-primary shrink-0 sm:ml-auto"
          onClick={() => {
            setSelectedClub(null);
            setShowClubModal(true);
          }}
        >
          Add Club
        </button>
      </div>

      {error && <div className="panel px-4 py-3 text-sm text-accent-soft border-accent-dim">{error}</div>}

      <div className="card overflow-hidden">
        {loading ? (
          <div className="py-14 flex flex-col items-center text-gray-400">
            <div className="w-8 h-8 border-2 border-line-strong border-t-accent rounded-full animate-spin" />
            <p className="mt-4 text-sm">Loading clubs…</p>
          </div>
        ) : filtered.length === 0 ? (
          <div className="py-12 text-center">
            <p className="text-base font-semibold text-white">
              {search || sportFilter !== "all" ? "No clubs match your filters" : "No clubs yet"}
            </p>
            <p className="mt-1 text-sm text-gray-500">
              {search || sportFilter !== "all"
                ? "Try adjusting your search or sport filter."
                : "Add your first club to get started."}
            </p>
          </div>
        ) : (
        <div className="overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead>
              <tr className="table-head">
                <th className="px-3.5 py-2.5 text-left w-14">Logo</th>
                <th className="px-3.5 py-2.5 text-left">Club name</th>
                <th className="px-3.5 py-2.5 text-left">Sport</th>
                <th className="px-3.5 py-2.5 text-left">Location</th>
                <th className="px-3.5 py-2.5 text-left">Founded</th>
                <th className="px-3.5 py-2.5 text-right sticky right-0 bg-surface-600 z-10 border-l border-line-strong">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((club) => (
                <tr key={club.clubId} className="table-row group">
                  <td className="px-3.5 py-2.5">
                    {club.logo ? (
                      <img
                        key={`img-${club.clubId}`}
                        src={club.logo}
                        alt={`${club.name} logo`}
                        className="w-9 h-9 object-cover rounded-full border border-line-strong"
                        onError={(e) => {
                          e.target.style.display = "none";
                        }}
                      />
                    ) : (
                      <div
                        key={`placeholder-${club.clubId}`}
                        className="w-9 h-9 bg-surface-700 border border-line-strong rounded-full flex items-center justify-center text-xs font-bold text-accent-soft"
                      >
                        {club.name?.charAt(0)}
                      </div>
                    )}
                  </td>
                  <td className="px-3.5 py-2.5 text-white font-medium">{club.name}</td>
                  <td className="px-3.5 py-2.5">
                    <span className="badge-neutral">{getSportName(club.sportId)}</span>
                  </td>
                  <td className="px-3.5 py-2.5 text-gray-400">{club.location || "—"}</td>
                  <td className="px-3.5 py-2.5 text-gray-400 tabular-nums">{club.foundedYear || "—"}</td>
                  <td className="px-3.5 py-2 sticky right-0 bg-surface-850 group-hover:bg-surface-700 border-b border-l border-line transition-colors">
                    <div className="flex items-center justify-end gap-2">
                      <button
                        key={`edit-${club.clubId}`}
                        type="button"
                        className="btn-ghost !py-1.5 !px-3"
                        onClick={() => {
                          setSelectedClub(club);
                          setShowClubModal(true);
                        }}
                      >
                        Edit
                      </button>
                      <button
                        key={`delete-${club.clubId}`}
                        type="button"
                        className="btn-danger !py-1.5 !px-3"
                        onClick={() => setClubToDelete(club)}
                      >
                        Delete
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        )}
      </div>

      {clubToDelete && (
        <ConfirmDialog
          title="Delete club"
          message={`Delete “${clubToDelete.name}”? Its league standings and league memberships will also be removed.`}
          confirmLabel="Delete club"
          onConfirm={handleDeleteClub}
          onCancel={() => setClubToDelete(null)}
          busy={loading}
        />
      )}

      {showClubModal && (
        <ClubModal
          club={selectedClub}
          sports={sports}
          onClose={() => {
            setShowClubModal(false);
            setSelectedClub(null);
          }}
          onSubmit={selectedClub ? handleEditClub : handleAddClub}
        />
      )}
    </div>
  );
};

export default ClubsTab;
